/**
 * Sync Queue for offline mutations
 * Stores pending mutations in localStorage and retries when online
 */

export interface PendingMutation {
  id: string;
  timestamp: number;
  type: 'addSet' | 'deleteSet' | 'finishWorkout' | 'createWorkout';
  payload: unknown;
  retryCount: number;
}

const STORAGE_KEY = 'gymlab-pending-mutations';

class SyncQueue {
  private queue: PendingMutation[] = [];
  private isProcessing = false;
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.load();
    this.setupOnlineListener();
  }

  private load() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.queue = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to load sync queue:', e);
      this.queue = [];
    }
  }

  private save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));
    } catch (e) {
      console.warn('Failed to save sync queue:', e);
    }
  }

  private setupOnlineListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.processQueue();
      });
    }
  }

  add(mutation: Omit<PendingMutation, 'id' | 'timestamp' | 'retryCount'>) {
    const pendingMutation: PendingMutation = {
      ...mutation,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.queue.push(pendingMutation);
    this.save();
    this.notifyListeners();

    // Try to process immediately if online
    if (navigator.onLine) {
      this.processQueue();
    }
  }

  remove(id: string) {
    this.queue = this.queue.filter(m => m.id !== id);
    this.save();
    this.notifyListeners();
  }

  getQueue(): PendingMutation[] {
    return [...this.queue];
  }

  getPendingCount(): number {
    return this.queue.length;
  }

  async processQueue() {
    if (this.isProcessing || this.queue.length === 0 || !navigator.onLine) {
      return;
    }

    this.isProcessing = true;

    for (const mutation of [...this.queue]) {
      try {
        await this.executeMutation(mutation);
        this.remove(mutation.id);
      } catch (e) {
        console.warn('Failed to process mutation:', mutation, e);
        
        // Increment retry count
        mutation.retryCount++;
        
        // Remove if too many retries
        if (mutation.retryCount >= 5) {
          this.remove(mutation.id);
          console.warn('Mutation removed after max retries:', mutation);
        } else {
          this.save();
        }
      }
    }

    this.isProcessing = false;
    this.notifyListeners();
  }

  private async executeMutation(mutation: PendingMutation): Promise<void> {
    const { type, payload } = mutation;

    switch (type) {
      case 'addSet': {
        const { workoutId, data } = payload as { workoutId: string; data: { exercise_id: string; weight: number; reps: number } };
        const response = await fetch(`/api/workouts/${workoutId}/sets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`Failed to add set: ${response.status}`);
        break;
      }

      case 'deleteSet': {
        const { workoutId, setId } = payload as { workoutId: string; setId: string };
        const response = await fetch(`/api/workouts/${workoutId}/sets/${setId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`Failed to delete set: ${response.status}`);
        break;
      }

      case 'finishWorkout': {
        const { workoutId } = payload as { workoutId: string };
        const response = await fetch(`/api/workouts/${workoutId}/finish`, {
          method: 'PATCH',
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`Failed to finish workout: ${response.status}`);
        break;
      }

      case 'createWorkout': {
        const { date } = payload as { date: string };
        const response = await fetch('/api/workouts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date }),
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`Failed to create workout: ${response.status}`);
        break;
      }

      default:
        throw new Error(`Unknown mutation type: ${type}`);
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }
}

// Singleton instance
export const syncQueue = new SyncQueue();
